/**
 * /income-statement — Profit & Loss for a date range.
 *
 * Pick from + to dates, get revenue + expense activity within that
 * window. Net income = total revenue - total expenses (positive =
 * profit, negative = loss).
 *
 * Default range = year-to-date (Jan 1 → today). Quick-pick buttons
 * jump to common ranges: this month, last month, this quarter, YTD,
 * last year. Operators close monthly + quarterly + annual on this page.
 *
 * Backend computes from posted journal lines whose journal_date falls
 * in the range. Expense balances are flipped to natural-debit so they
 * read positive.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";

import {
  consolidatedIncomeStatementQueryOptions,
  glDimensionDetailQueryOptions,
  glDimensionsQueryOptions,
  incomeStatementQueryOptions,
  myEntitiesQueryOptions,
} from "@/lib/queries";
import { FsSection, fmtMoney } from "@/components/FinancialStatementSection";
import type { GlDimensionValue } from "@/lib/types";

function flattenValues(
  nodes: GlDimensionValue[],
  depth = 0,
): Array<{ id: string; label: string }> {
  return nodes.flatMap((n) => [
    { id: n.id, label: " ".repeat(depth * 3) + (depth > 0 ? "└ " : "") + n.name },
    ...flattenValues(n.children, depth + 1),
  ]);
}

export const Route = createFileRoute("/_auth/income-statement")({
  component: IncomeStatementPage,
});

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function startOfQuarter(d: Date): Date {
  return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
}

function startOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1);
}

function IncomeStatementPage() {
  const today = new Date();
  // Default to YTD
  const [periodFrom, setPeriodFrom] = useState(isoDate(startOfYear(today)));
  const [periodTo, setPeriodTo] = useState(isoDate(today));

  const [consolidated, setConsolidated] = useState(false);
  const [dimCode, setDimCode] = useState<string>("");
  const [dimValueId, setDimValueId] = useState<string>("");

  const entitiesQuery = useQuery(myEntitiesQueryOptions());
  const currentEntity = entitiesQuery.data?.find((e) => e.is_current);
  const canConsolidate = currentEntity?.role === "admin";

  // Dimension filter support
  const dimsListQuery = useQuery(glDimensionsQueryOptions());
  const activeDims = useMemo(
    () => (dimsListQuery.data ?? []).filter((d) => d.is_active),
    [dimsListQuery.data],
  );
  const selectedDimId = activeDims.find((d) => d.code === dimCode)?.id ?? "";
  const [dimDetailQuery] = useQueries({
    queries: [glDimensionDetailQueryOptions(selectedDimId)],
  });
  const flatValues = useMemo(
    () => (dimDetailQuery.data ? flattenValues(dimDetailQuery.data.values) : []),
    [dimDetailQuery.data],
  );
  const dimActive = Boolean(dimCode && dimValueId);
  const selectedDimMeta = activeDims.find((d) => d.code === dimCode);
  const selectedValueLabel = flatValues.find((v) => v.id === dimValueId)?.label.trim();

  const entityIsQuery = useQuery({
    ...incomeStatementQueryOptions(
      periodFrom, periodTo,
      dimCode || undefined,
      dimValueId || undefined,
    ),
    enabled: !consolidated,
  });
  const consolIsQuery = useQuery({
    ...consolidatedIncomeStatementQueryOptions(
      currentEntity?.id ?? "", periodFrom, periodTo,
    ),
    enabled: consolidated && Boolean(currentEntity?.id),
  });
  const isQuery = consolidated ? consolIsQuery : entityIsQuery;
  const stmt = isQuery.data;

  // Quick-pick buttons (set both dates at once)
  const quickPicks = useMemo(() => {
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastYear = new Date(today.getFullYear() - 1, 0, 1);
    return [
      { label: "This month", from: isoDate(startOfMonth(today)), to: isoDate(today) },
      {
        label: "Last month",
        from: isoDate(startOfMonth(lastMonth)),
        to: isoDate(endOfMonth(lastMonth)),
      },
      { label: "This quarter", from: isoDate(startOfQuarter(today)), to: isoDate(today) },
      { label: "YTD", from: isoDate(startOfYear(today)), to: isoDate(today) },
      {
        label: "Last year",
        from: isoDate(lastYear),
        to: isoDate(new Date(lastYear.getFullYear(), 11, 31)),
      },
    ];
  }, []);

  const netIncome = stmt ? Number(stmt.net_income) : 0;
  const isProfit = netIncome >= 0;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header / controls */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 print:hidden dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Income Statement
          </h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Revenue − Expenses for the chosen date range · accrual basis
          </span>
          <div className="flex-1" />
          {canConsolidate && (
            <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer dark:text-slate-300"
                   title="Roll up this entity + all subsidiaries, eliminating intercompany Due-from/Due-to pairs">
              <input
                type="checkbox"
                checked={consolidated}
                onChange={(e) => setConsolidated(e.target.checked)}
              />
              Consolidated
            </label>
          )}
          {isQuery.isFetching && (
            <span className="text-xs text-slate-400 dark:text-slate-500">Loading…</span>
          )}
        </div>

        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            From:
            <input
              type="date"
              value={periodFrom}
              onChange={(e) => setPeriodFrom(e.target.value)}
              className="text-sm border border-slate-300 rounded-md px-2 py-1.5 dark:border-gray-700"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            To:
            <input
              type="date"
              value={periodTo}
              onChange={(e) => setPeriodTo(e.target.value)}
              className="text-sm border border-slate-300 rounded-md px-2 py-1.5 dark:border-gray-700"
            />
          </label>

          {/* Quick-pick row */}
          <div className="flex items-center gap-1.5 ml-2">
            {quickPicks.map((q) => (
              <button
                key={q.label}
                onClick={() => { setPeriodFrom(q.from); setPeriodTo(q.to); }}
                className="text-xs border border-slate-300 hover:bg-slate-100 rounded-md px-2 py-1 text-slate-700 dark:border-gray-700 dark:text-slate-300 dark:hover:bg-gray-800"
              >
                {q.label}
              </button>
            ))}
          </div>

          <div className="flex-1" />
          <button
            onClick={() => window.print()}
            className="text-sm border border-slate-300 hover:bg-slate-100 rounded-md px-3 py-1.5 dark:border-gray-700 dark:hover:bg-gray-800"
            title="Print or save as PDF"
          >
            🖨 Print
          </button>
        </div>

        {/* Dimension filter row — only shown when dimensions are configured */}
        {activeDims.length > 0 && (
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
              Slice by
            </span>
            <select
              value={dimCode}
              onChange={(e) => { setDimCode(e.target.value); setDimValueId(""); }}
              className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-900 dark:border-gray-700"
            >
              <option value="">— dimension —</option>
              {activeDims.map((d) => (
                <option key={d.id} value={d.code}>{d.name}</option>
              ))}
            </select>

            {dimCode && (
              <select
                value={dimValueId}
                onChange={(e) => setDimValueId(e.target.value)}
                disabled={dimDetailQuery.isLoading}
                className="border border-indigo-400 rounded-md px-2 py-1 text-sm bg-white min-w-[180px] dark:bg-gray-900 dark:border-indigo-600"
              >
                <option value="">— pick value —</option>
                {flatValues.map((v) => (
                  <option key={v.id} value={v.id}>{v.label}</option>
                ))}
              </select>
            )}

            {dimActive && (
              <button
                type="button"
                onClick={() => { setDimCode(""); setDimValueId(""); }}
                className="text-xs text-indigo-600 hover:text-indigo-800 border border-indigo-300 rounded-md px-2 py-1 dark:text-indigo-400"
              >
                ✕ Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Statement body */}
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="max-w-3xl mx-auto px-6 py-8 print:py-4">
          {isQuery.error && (
            <div className="text-sm text-red-600">
              Error: {(isQuery.error as Error).message}
            </div>
          )}

          {/* Dimension slice banner */}
          {dimActive && selectedDimMeta && selectedValueLabel && (
            <div className="mb-4 flex items-center gap-2 text-sm bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2.5 print:hidden dark:bg-indigo-950/30 dark:border-indigo-800">
              <span className="text-indigo-700 dark:text-indigo-300 font-medium">Sliced by:</span>
              <span className="font-mono text-xs bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200 px-2 py-0.5 rounded border border-indigo-200 dark:border-indigo-700">
                {selectedDimMeta.name} = {selectedValueLabel}
              </span>
              <span className="text-indigo-500 dark:text-indigo-400 text-xs">
                · Only revenue and expense lines tagged with this segment are included
              </span>
            </div>
          )}

          {stmt && (
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 print:border-0 print:shadow-none print:p-0 dark:bg-gray-900 dark:border-gray-800">
              {/* Statement header (printable) */}
              <div className="text-center mb-6 pb-4 border-b border-slate-300 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {consolidated ? "Consolidated Income Statement" : "Income Statement"}
                  {dimActive && selectedDimMeta && selectedValueLabel && (
                    <span className="block text-sm font-normal text-indigo-600 dark:text-indigo-400 mt-0.5">
                      {selectedDimMeta.name}: {selectedValueLabel}
                    </span>
                  )}
                </h2>
                {consolidated && currentEntity && (
                  <p className="text-xs text-slate-500 mt-0.5 dark:text-slate-400">
                    {currentEntity.display_name} + subsidiaries · intercompany eliminated
                  </p>
                )}
                <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">
                  For the period {stmt.period_from} through {stmt.period_to}
                </p>
              </div>

              {/* Revenue */}
              <FsSection section={stmt.revenue} />

              {/* Expenses */}
              <FsSection section={stmt.expenses} />

              {/* Net income */}
              <div className="mt-6 pt-3 border-t-2 border-slate-900 space-y-1">
                <div className="flex items-baseline">
                  <strong className="text-sm uppercase tracking-wide text-slate-900 dark:text-slate-100">
                    Total Revenue
                  </strong>
                  <span className="flex-1" />
                  <span className="font-mono font-semibold text-slate-900 text-base dark:text-slate-100">
                    {fmtMoney(stmt.revenue.total)}
                  </span>
                </div>
                <div className="flex items-baseline">
                  <strong className="text-sm uppercase tracking-wide text-slate-900 dark:text-slate-100">
                    Total Expenses
                  </strong>
                  <span className="flex-1" />
                  <span className="font-mono font-semibold text-slate-900 text-base dark:text-slate-100">
                    ({fmtMoney(stmt.expenses.total)})
                  </span>
                </div>

                <div className="mt-3 pt-3 border-t border-slate-200 flex items-baseline dark:border-gray-800">
                  <strong className="text-base uppercase tracking-wide text-slate-900 dark:text-slate-100">
                    Net {isProfit ? "Income" : "Loss"}
                  </strong>
                  <span className="flex-1" />
                  <span
                    className={`font-mono font-bold text-lg ${
                      isProfit ? "text-emerald-700" : "text-red-700"
                    }`}
                  >
                    {fmtMoney(stmt.net_income)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {isQuery.isLoading && !stmt && (
            <div className="text-sm text-slate-500 text-center py-8 dark:text-slate-400">
              Loading income statement…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
